import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useUserContext } from "../../context/UserContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import {
  checkLetsLaunchAccess,
  getUserById,
} from "../../client/api";
import { setSsoCookie } from "../../utils/ssoCookie";

// SpikeUp Solutions Ops Command brand system - see spikeup-solutions-brand-book.html
const THEME_COLORS = {
  bg: "#04070a",
  panel: "#0b1318",
  panel2: "#0e1a20",
  bezel: "#1b262d",
  bezelHi: "#2c3b44",
  line: "#16242b",
  cyan: "#39e0ff",
  cyanGlow: "rgba(57,224,255,.5)",
  red: "#ff4b3e",
  txt: "#9fc6c0",
  txtDim: "#5b7a78",
  ink: "#cdeee6",
} as const;

const FONT_DISPLAY = "'Orbitron', sans-serif";
const FONT_UI = "'Rajdhani', sans-serif";
const FONT_MONO = "'Share Tech Mono', monospace";

const PAGE_BG_SX = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `radial-gradient(120% 80% at 50% -8%, #0a1820 0%, ${THEME_COLORS.bg} 55%), ${THEME_COLORS.bg}`,
  fontFamily: FONT_UI,
  padding: 2,
  position: "relative" as const,
  overflow: "hidden" as const,
  "&::before": {
    content: '""',
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none" as const,
    mixBlendMode: "overlay" as const,
    opacity: 0.45,
    background:
      "repeating-linear-gradient(0deg, rgba(0,0,0,.35) 0 1px, transparent 1px 3px)",
  },
};

const PANEL_SX = {
  maxWidth: 400,
  width: "100%",
  padding: 4,
  borderRadius: "11px",
  background: `linear-gradient(180deg, ${THEME_COLORS.panel2}, ${THEME_COLORS.panel})`,
  border: `1px solid ${THEME_COLORS.bezel}`,
  boxShadow: `inset 0 1px 0 ${THEME_COLORS.bezelHi}, inset 0 0 22px rgba(0,0,0,.6), 0 6px 18px rgba(0,0,0,.5)`,
  position: "relative" as const,
  zIndex: 1,
};

const AuthSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setCurrentWorkspace } = useUserContext();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const handleAuthSuccess = async () => {
      try {
        const token = searchParams.get("token");
        const refreshToken = searchParams.get("refreshToken");
        const userId = searchParams.get("userId");

        if (!token || !userId) {
          setError("Authentication token missing");
          setLoading(false);
          return;
        }

        // Store token for future API calls. Cognito access tokens don't
        // carry a userId claim the way the old app-issued JWT did, so
        // userId comes from the redirect URL now, not decoded client-side
        // from the token itself.
        localStorage.setItem("token", token);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }
        // Makes zapflow/farm-manager reachable via the app-picker right
        // after this. Not threading a `redirect` param through the full
        // Google OAuth round-trip (frontend -> backend -> Google ->
        // backend -> here) for now - a user bounced from one of those
        // apps just lands on letslaunch's own app-selection screen and
        // clicks through once more, which now works since this cookie
        // is already set.
        setSsoCookie(token);
        localStorage.setItem("google_access_token", token);
        Cookies.set("google_access_token", token, {
          expires: 5,
        });

        // Get user data using the userId from the redirect URL
        const userData = await getUserById(userId);

        if (!userData) {
          setError("Failed to fetch user data");
          setLoading(false);
          return;
        }

        // Set user context
        setUser(userData);
        setCurrentWorkspace(userData.ownedWorkspaces[0]);

        // Store user details in cookie (same as regular login)
        const cookieData = {
          userId: userData._id,
          organizationKey: userData.organization.organizationKey,
          letsLaunchAccess: userData.organization.letsLaunchAccess,
        };

        Cookies.set("authInfo", JSON.stringify(cookieData), {
          domain: process.env.REACT_APP_COOKIE_DOMAIN,
          path: "/",
          secure: true,
          sameSite: "strict",
          expires: 5,
        });

        localStorage.setItem("userId", userData._id);

        // Resume pending workspace invitation if present
        const invitationUrl = localStorage.getItem("invitationUrl");
        if (invitationUrl) {
          localStorage.removeItem("invitationUrl");
          navigate(invitationUrl);
          return;
        }

        // Handle redirect logic (same as regular login - see Login.tsx's
        // handleLogin/checkUserFromCookie for the identical counting logic).
        let hasLetsLaunchAccess = false;
        if (userData.organization) {
          hasLetsLaunchAccess = await checkLetsLaunchAccess(
            userData.organization.organizationKey
          );
        }
        const hasZapflowAccess = !!userData.zapflowAccess;
        const hasFarmSystemAccess = !!userData.farmSystemAccess;

        const APP_URLS: Record<string, string> = {
          zapflow: process.env.REACT_APP_ZAPFLOW_URL || "https://zapflow.letslaunch.io",
          farmsystem: process.env.REACT_APP_FARMSYSTEM_URL || "https://farmsystem.letslaunch.io",
        };
        const otherApps = [
          hasZapflowAccess && "zapflow",
          hasFarmSystemAccess && "farmsystem",
        ].filter(Boolean) as string[];
        const totalAccessCount = (hasLetsLaunchAccess ? 1 : 0) + otherApps.length;

        if (!hasLetsLaunchAccess && otherApps.length === 1) {
          window.location.href = APP_URLS[otherApps[0]];
        } else if (totalAccessCount > 1) {
          navigate("/app-selection");
        } else {
          navigate("/");
        }
      } catch (error: any) {
        console.error("Auth success error:", error);
        setError(error.message || "Authentication failed");
        setLoading(false);
      }
    };

    handleAuthSuccess();
  }, [searchParams, navigate, setUser, setCurrentWorkspace]);

  if (loading) {
    return (
      <Box sx={PAGE_BG_SX}>
        <Box
          sx={{
            ...PANEL_SX,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <CircularProgress
            sx={{
              color: THEME_COLORS.cyan,
              filter: `drop-shadow(0 0 8px ${THEME_COLORS.cyanGlow})`,
              mb: 2,
            }}
          />
          <Typography
            sx={{
              fontFamily: FONT_MONO,
              color: THEME_COLORS.txtDim,
              letterSpacing: "2px",
              fontSize: "12px",
              textTransform: "uppercase",
            }}
          >
            Completing authentication...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={PAGE_BG_SX}>
        <Box sx={PANEL_SX}>
          <Alert
            severity="error"
            sx={{
              backgroundColor: "rgba(255,75,62,0.08)",
              border: `1px solid ${THEME_COLORS.red}`,
              borderLeft: `4px solid ${THEME_COLORS.red}`,
              color: THEME_COLORS.ink,
              borderRadius: "8px",
              fontFamily: FONT_UI,
              "& .MuiAlert-icon": { color: THEME_COLORS.red },
            }}
          >
            {error}
          </Alert>
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button
              onClick={() => navigate("/login")}
              variant="contained"
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: "7px",
                background: "linear-gradient(180deg, #13343f, #0a1c22)",
                border: `1px solid ${THEME_COLORS.bezelHi}`,
                color: THEME_COLORS.cyan,
                fontFamily: FONT_DISPLAY,
                fontSize: "0.8rem",
                fontWeight: 700,
                letterSpacing: "2px",
                textShadow: `0 0 10px ${THEME_COLORS.cyanGlow}`,
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(180deg, #17404c, #0d232a)",
                  borderColor: THEME_COLORS.cyan,
                  boxShadow: "0 0 14px rgba(57,224,255,0.25)",
                },
              }}
            >
              Back to Login
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

export default AuthSuccess;
